/** Text normalization shared with ATS keyword matching (parity with scraper/core/keywords/normalize.py). */

export function normalizeMatchText(text) {
  if (!text) return "";

  let value = String(text).normalize("NFKC");
  value = value.replace(/\u00a0/g, " ");
  value = value.replace(/[\u2018\u2019]/g, "'");
  value = value.toLowerCase();
  value = value.replace(/&/g, " and ");
  value = value.replace(/\s+/g, " ").trim();
  return value;
}

export function normalizePattern(text) {
  let normalized = normalizeMatchText(text);
  normalized = normalized.replace(/&/g, " and ");
  normalized = normalized.replace(/\s+/g, " ").trim();
  return normalized;
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Short dictionary tokens that collide with English must match capitalized forms:
 * - ALL CAPS 1–2 letters (IT, AI, UX, R): exact uppercase only ("IT" ≠ "it" ≠ "It")
 * - Title Case 2 letters (Go): "Go" or "GO", never the verb "go"
 * @param {string | null | undefined} pattern
 */
export function isCaseSensitiveShortToken(pattern) {
  const p = String(pattern || "").trim();
  if (p.length >= 1 && p.length <= 2 && /^[A-Z]+$/.test(p)) return true;
  if (p.length === 2 && /^[A-Z][a-z]$/.test(p)) return true;
  return false;
}

/** @param {string | null | undefined} pattern */
export function isShortUpperAlias(pattern) {
  return isCaseSensitiveShortToken(pattern);
}

/**
 * Allowed surface spellings for a short case-sensitive token.
 * @param {string | null | undefined} pattern
 * @returns {string[]}
 */
export function shortTokenMatchForms(pattern) {
  const p = String(pattern || "").trim();
  if (!isCaseSensitiveShortToken(p)) return [p];
  if (/^[A-Z]+$/.test(p)) return [p];
  return p === p.toUpperCase() ? [p] : [p, p.toUpperCase()];
}

/** Whitespace-normalize without lowercasing — for short UPPER alias matching. */
export function preserveCaseMatchText(text) {
  if (!text) return "";
  let value = String(text).normalize("NFKC");
  value = value.replace(/\u00a0/g, " ");
  value = value.replace(/\s+/g, " ").trim();
  return value;
}

/**
 * Whole-phrase ATS match. Short tokens (Go, IT, R) require capitalized forms
 * on the original text; everything else uses normalized lowercase haystack.
 * @param {string} haystack
 * @param {string} phrase
 */
export function textHasKeywordPhrase(haystack, phrase) {
  const raw = String(phrase || "").trim();
  if (!raw) return false;

  if (isCaseSensitiveShortToken(raw)) {
    const source = preserveCaseMatchText(haystack);
    const body = shortTokenMatchForms(raw).map(escapeRegExp).join("|");
    try {
      return new RegExp(`(?<!\\w)(?:${body})(?!\\w)`).test(source);
    } catch {
      return false;
    }
  }

  const needle = normalizePattern(raw);
  if (!needle || needle.length < 2) return false;
  const haystackNormalized = normalizeMatchText(haystack);
  try {
    return new RegExp(`(?<!\\w)${escapeRegExp(needle)}(?!\\w)`, "i").test(haystackNormalized);
  } catch {
    return false;
  }
}

/** Collapse messy extracted resume text for storage / re-parse. */
export function normalizeResumeText(text) {
  if (!text) return "";

  let value = String(text).normalize("NFKC");
  value = value.replace(/\u00a0/g, " ");
  value = value.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  value = value.replace(/[ \t]+\n/g, "\n");
  value = value.replace(/\n{3,}/g, "\n\n");
  value = value.replace(/[ \t]{2,}/g, " ");
  return value.trim();
}
